import React, { FC, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useSendContactForm } from 'services/contact'
import { useFormFields } from 'hooks/useFormFields'
import { Button } from 'components/Button'
import { Dialog } from 'components/Dialog'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import { TextInput } from 'components/TextInput'
import { Alert } from 'components/Alert'

type ContactDialogProps = {
  action?: string
  isOpen: boolean
  closeDialog: () => void
}

export const ContactDialog: FC<ContactDialogProps> = ({ action, isOpen, closeDialog }) => {
  const { t } = useTranslation()
  const [contact, { status }] = useSendContactForm()

  const [form, setForm] = useFormFields({
    name: '',
    email: '',
    phoneNumber: '',
    company: '',
    companyWebsite: '',
    industry: '',
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    contact(form, {
      onSuccess() {
        setTimeout(() => closeDialog(), 500)
      },
    })
  }

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => closeDialog()}
      aria-label={t('subscription.contact_dialog')}
    >
      <Stack>
        <Stack space="small">
          <Heading>{t(`subscription.${action}.heading`)}</Heading>
          <Text>{t(`subscription.${action}.description`)}</Text>
        </Stack>
        <form onSubmit={handleSubmit}>
          <Stack>
            <TextInput
              name="name"
              label={t('subscription.contact.name')}
              onChange={setForm}
              value={form.name}
              required
            />
            <TextInput
              name="email"
              label={t('subscription.contact.email')}
              onChange={setForm}
              value={form.email}
              required
            />
            <TextInput
              name="phoneNumber"
              label={t('subscription.contact.phone_number')}
              onChange={setForm}
              value={form.phoneNumber}
              required
            />
            <TextInput
              name="company"
              label={t('subscription.contact.company')}
              onChange={setForm}
              value={form.company}
            />
            <TextInput
              name="companyWebsite"
              label={t('subscription.contact.company_website')}
              onChange={setForm}
              value={form.companyWebsite}
            />
            <TextInput
              name="industry"
              label={t('subscription.contact.industry')}
              onChange={setForm}
              value={form.industry}
            />
            <Button type="submit" full loading={status === 'loading'}>
              {t('subscription.contact.submit')}
            </Button>

            {status === 'success' && (
              <Alert tone="positive" full>
                {t('common.messages.success')}
              </Alert>
            )}

            {status === 'error' && (
              <Alert tone="critical" full>
                {t('common.messages.error')}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Dialog>
  )
}

ContactDialog.displayName = 'ContactDialog'
